import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Drawer,
  Fab,
  IconButton,
  Link as MuiLink,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import ChatIcon from '@mui/icons-material/Chat';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { alpha } from '@mui/material/styles';
import { api } from '../contexts/ApiContext';

/** Classification review page opens Quickview (default tab: Other). */
export const OPEN_RULES_QUICKVIEW_EVENT = 'family-fin:open-rules-quickview';
/** Optional app-wide event pages can emit to open Quickview with custom data payload. */
export const OPEN_APP_QUICKVIEW_EVENT = 'family-fin:open-app-quickview';

type ChatRole = 'user' | 'assistant';
type AppQuickviewTab = 'chat' | 'other';

type ChatMessage = {
  role: ChatRole;
  content: string;
  suggestions?: { label: string; to: string }[];
};

function safeJsonStringify(value: unknown): string {
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return '"Could not serialize data payload."';
  }
}

export const AppAssistantFab: React.FC = () => {
  const theme = useTheme();
  const loc = useLocation();
  const [open, setOpen] = useState(false);
  const [quickviewTab, setQuickviewTab] = useState<AppQuickviewTab>('other');
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [otherSnapshot, setOtherSnapshot] = useState<{ title: string; data: unknown } | null>(null);

  const buildDefaultOtherSnapshot = useCallback(
    () => ({
      title: 'Page context',
      data: {
        pathname: loc.pathname,
        search: loc.search || '',
        opened_at: new Date().toISOString(),
      },
    }),
    [loc.pathname, loc.search]
  );

  const openAppQuickview = useCallback(
    (opts?: { tab?: AppQuickviewTab; title?: string; data?: unknown }) => {
      setQuickviewTab(opts?.tab || 'other');
      if (opts && ('title' in opts || 'data' in opts)) {
        setOtherSnapshot({
          title: (opts.title || '').trim() || 'Data snapshot',
          data: opts.data ?? null,
        });
      } else {
        setOtherSnapshot(buildDefaultOtherSnapshot());
      }
      setOpen(true);
    },
    [buildDefaultOtherSnapshot]
  );

  useEffect(() => {
    const onOpenQuickview = (ev: Event) => {
      const detail = (ev as CustomEvent<{ tab?: AppQuickviewTab; title?: string; data?: unknown }>).detail || {};
      openAppQuickview(detail);
    };
    window.addEventListener(OPEN_APP_QUICKVIEW_EVENT, onOpenQuickview as EventListener);
    return () => window.removeEventListener(OPEN_APP_QUICKVIEW_EVENT, onOpenQuickview as EventListener);
  }, [openAppQuickview]);

  const send = useCallback(async () => {
    const text = input.trim();
    if (!text || loading) return;

    const nextMessages: ChatMessage[] = [...messages, { role: 'user', content: text }];
    setMessages(nextMessages);
    setInput('');
    setError(null);
    setLoading(true);

    try {
      const payload = {
        messages: nextMessages.map(({ role, content }) => ({ role, content })),
        page: { pathname: loc.pathname, search: loc.search || '' },
      };
      const res = await api.post<{ reply?: string; suggestions?: { label: string; to: string }[]; message?: string }>(
        '/api/app-assistant/chat',
        payload
      );
      const reply = (res.data.reply || '').trim();
      const suggestions = Array.isArray(res.data.suggestions) ? res.data.suggestions : [];
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: reply || 'No response text returned.', suggestions },
      ]);
    } catch (e: any) {
      const msg =
        e?.response?.data?.message ||
        e?.response?.data?.error ||
        e?.message ||
        'Request failed';
      setError(String(msg));
      setMessages((prev) => prev.slice(0, -1));
    } finally {
      setLoading(false);
    }
  }, [input, loading, loc.pathname, loc.search, messages]);

  const handleKeyDown = (ev: React.KeyboardEvent) => {
    if (ev.key === 'Enter' && !ev.shiftKey) {
      ev.preventDefault();
      void send();
    }
  };

  const effectiveOtherSnapshot = useMemo(
    () => otherSnapshot || buildDefaultOtherSnapshot(),
    [otherSnapshot, buildDefaultOtherSnapshot]
  );
  const otherSnapshotJson = useMemo(
    () => safeJsonStringify(effectiveOtherSnapshot.data),
    [effectiveOtherSnapshot.data]
  );

  return (
    <>
      <Fab
        color="primary"
        aria-label={
          loc.pathname === '/transaction-classification-review'
            ? 'Open quickview assistant'
            : 'Open app assistant'
        }
        onClick={() => {
          if (loc.pathname === '/transaction-classification-review') {
            window.dispatchEvent(
              new CustomEvent(OPEN_RULES_QUICKVIEW_EVENT, { detail: { tab: 'other' as const } })
            );
            return;
          }
          openAppQuickview({ tab: 'other' });
        }}
        sx={{
          position: 'fixed',
          right: 24,
          bottom: 24,
          zIndex: theme.zIndex.drawer + 2,
          display: open ? 'none' : 'flex',
        }}
      >
        <ChatIcon />
      </Fab>

      <Drawer
        anchor="right"
        open={open}
        onClose={() => !loading && setOpen(false)}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 520, md: 620 },
            maxWidth: '100vw',
            borderLeft: `1px solid ${theme.palette.divider}`,
          },
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', bgcolor: 'background.paper' }}>
          <Box
            sx={{
              flexShrink: 0,
              px: 2,
              pt: 1.25,
              pb: 1,
              background:
                theme.palette.mode === 'dark'
                  ? `linear-gradient(145deg, ${alpha(theme.palette.primary.main, 0.2)} 0%, ${alpha(
                      theme.palette.common.black,
                      0.2
                    )} 55%, ${alpha(theme.palette.background.paper, 1)} 100%)`
                  : `linear-gradient(145deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(
                      theme.palette.primary.light,
                      0.18
                    )} 42%, ${theme.palette.background.paper} 100%)`,
            }}
          >
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1.5}>
              <Box
                sx={{
                  minWidth: 0,
                  flex: 1,
                  pr: 0.5,
                }}
              >
                <Typography variant="subtitle1" component="div" sx={{ fontWeight: 800, letterSpacing: -0.02 }}>
                  Quickview
                </Typography>
              </Box>
              <IconButton
                aria-label="Close quickview"
                onClick={() => !loading && setOpen(false)}
                edge="end"
                size="small"
                disabled={loading}
              >
                <CloseIcon />
              </IconButton>
            </Stack>
            <Tabs
              value={quickviewTab}
              onChange={(_, v) => setQuickviewTab(v as AppQuickviewTab)}
              aria-label="Quickview panel sections"
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              sx={{
                mt: 1,
                minHeight: 0,
                '& .MuiTab-root': {
                  minHeight: 40,
                  py: 0.75,
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.8125rem',
                  color: 'text.secondary',
                },
                '& .MuiTab-root.Mui-selected': {
                  color: 'text.primary',
                },
              }}
            >
              <Tab label="Chat" value="chat" id="qv-main-tab-chat-shared" aria-controls="qv-main-panel-chat-shared" />
              <Tab label="Other" value="other" id="qv-main-tab-other-shared" aria-controls="qv-main-panel-other-shared" />
            </Tabs>
          </Box>
          <Divider />
          {quickviewTab === 'chat' ? (
            <Box
              id="qv-main-panel-chat-shared"
              role="tabpanel"
              aria-labelledby="qv-main-tab-chat-shared"
              sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, minWidth: 0 }}
            >
              <Box sx={{ flex: 1, overflow: 'auto', px: 2, py: 2 }}>
                <Stack spacing={2}>
                  <Typography variant="body2" color="text.secondary">
                    Ask factual questions about bookings, property earnings, or Revolut payments — the
                    assistant looks up live data and answers directly. You can also ask how to open a
                    screen; answers may include links with filters filled in.
                  </Typography>
                  {error && (
                    <Alert severity="error" onClose={() => setError(null)}>
                      {error}
                    </Alert>
                  )}
                  {messages.length === 0 && !loading && (
                    <Typography variant="body2" color="text.secondary">
                      Example: &quot;Open classification review filtered to supplier Amazon&quot; or
                      &quot;Go to Revolut transaction focus_revolut_id …&quot;
                    </Typography>
                  )}
                  {messages.map((m, idx) => (
                    <Box
                      key={idx}
                      sx={{
                        alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start',
                        maxWidth: '100%',
                        px: 1.5,
                        py: 1,
                        borderRadius: 2,
                        bgcolor: m.role === 'user' ? 'primary.main' : 'action.hover',
                        color: m.role === 'user' ? 'primary.contrastText' : 'text.primary',
                      }}
                    >
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                        {m.content}
                      </Typography>
                      {m.role === 'assistant' && m.suggestions && m.suggestions.length > 0 && (
                        <Stack spacing={0.75} sx={{ mt: 1.5 }}>
                          <Typography variant="caption" sx={{ opacity: 0.85 }}>
                            Go to
                          </Typography>
                          {m.suggestions.map((s, j) => (
                            <MuiLink
                              key={j}
                              component={RouterLink}
                              to={s.to}
                              onClick={() => setOpen(false)}
                              underline="hover"
                              sx={{
                                fontWeight: 500,
                                color: m.role === 'user' ? 'inherit' : 'primary.main',
                                wordBreak: 'break-all',
                              }}
                            >
                              {s.label}
                            </MuiLink>
                          ))}
                        </Stack>
                      )}
                    </Box>
                  ))}
                  {loading && (
                    <Stack direction="row" spacing={1} alignItems="center">
                      <CircularProgress size={18} />
                      <Typography variant="body2" color="text.secondary">
                        Thinking… If the provider is rate-limiting, the server may retry for up to about a minute.
                      </Typography>
                    </Stack>
                  )}
                </Stack>
              </Box>
              <Divider />
              <Box sx={{ flexShrink: 0, px: 2, py: 1.5 }}>
                <Stack direction="row" spacing={1} alignItems="flex-end">
                  <TextField
                    fullWidth
                    multiline
                    minRows={1}
                    maxRows={4}
                    placeholder="Ask the assistant…"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    disabled={loading}
                    size="small"
                  />
                  <Button
                    variant="contained"
                    endIcon={<SendIcon />}
                    onClick={() => void send()}
                    disabled={loading || !input.trim()}
                  >
                    Send
                  </Button>
                </Stack>
              </Box>
            </Box>
          ) : null}
          {quickviewTab === 'other' ? (
            <Box
              id="qv-main-panel-other-shared"
              role="tabpanel"
              aria-labelledby="qv-main-tab-other-shared"
              sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, minWidth: 0 }}
            >
              <Box sx={{ px: 2.25, pt: 1.75, pb: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  {effectiveOtherSnapshot.title}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, lineHeight: 1.5 }}>
                  Generic data tab for all pages. You can override this payload later per page by dispatching
                  <Box component="span" sx={{ mx: 0.5, fontFamily: 'ui-monospace, monospace', fontSize: '0.72rem' }}>
                    {OPEN_APP_QUICKVIEW_EVENT}
                  </Box>
                  with custom detail.
                </Typography>
              </Box>
              <Divider />
              <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
                <Box
                  component="pre"
                  sx={{
                    m: 0,
                    p: 2,
                    borderRadius: 1,
                    bgcolor: 'grey.100',
                    border: '1px solid',
                    borderColor: 'divider',
                    fontFamily:
                      'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                    fontSize: '0.75rem',
                    lineHeight: 1.45,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                  }}
                >
                  {otherSnapshotJson}
                </Box>
              </Box>
            </Box>
          ) : null}
        </Box>
      </Drawer>
    </>
  );
};
